import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import {Mail, MapPin, Clock, Send} from 'lucide-react'
import { useState } from "react"

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    location: "",
    message: ""
  })

  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    setTimeout(() => setSubmitted(false), 5000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-slate-950" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-sky-500/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-sky-400 text-xs font-bold tracking-[0.2em] uppercase mb-6 block">
            Get In Touch
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6">
            Contact Us
            <br />
            <span className="text-slate-500">Today</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed">
            Ready to secure your property? Get a free consultation and quote from our security experts.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 sm:py-32 bg-slate-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-white">Get in Touch</h2>
              <p className="text-lg text-slate-400 mb-10 leading-relaxed">
                Our team is available 24/7 to discuss your security needs and provide immediate assistance.
              </p>

              <div className="space-y-6 mb-12">
                {[
                  {
                    icon: <Mail className="w-5 h-5" />,
                    title: "Email",
                    content: <a href="mailto:info@sentriosecurity.com" className="text-sky-400 hover:text-sky-300 transition-colors">info@sentriosecurity.com</a>,
                    sub: null
                  },
                  {
                    icon: <MapPin className="w-5 h-5" />,
                    title: "Service Areas",
                    content: <span className="text-slate-300">Serving major cities across Canada and the USA</span>,
                    sub: null
                  },
                  {
                    icon: <Clock className="w-5 h-5" />,
                    title: "Office Hours",
                    content: <span className="text-slate-300">Monday - Friday: 9:00 AM - 6:00 PM</span>,
                    sub: "Emergency Services: 24/7"
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{item.title}</h3>
                      <div className="text-sm">{item.content}</div>
                      {item.sub ? <p className="text-xs text-slate-500 mt-1">{item.sub}</p> : null}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="bg-slate-900 border border-slate-800/60 rounded-lg p-8">
                <h3 className="font-bold text-white mb-6">Quick Facts</h3>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: "<1hr", label: "Response Time" },
                    { value: "24/7", label: "Availability" },
                    { value: "500+", label: "Clients" },
                    { value: "7+", label: "Years" },
                  ].map((stat, i) => (
                    <div key={i}>
                      <div className="text-2xl font-bold text-sky-400 mb-1">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-slate-900 border border-slate-800/60 rounded-lg p-8 lg:p-10">
              <h2 className="text-2xl font-bold mb-2 text-white">Request a Quote</h2>
              <p className="text-sm text-slate-500 mb-8">Fill out the form below and we will get back to you within 24 hours.</p>

              {submitted ? (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-emerald-400 font-medium">Thank you! We will contact you within 24 hours.</p>
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all"
                      placeholder="(604) 555-1234"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    Service Type *
                  </label>
                  <select
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="uniformed">Uniformed Security Guards</option>
                    <option value="mobile">Mobile Patrol Services</option>
                    <option value="concierge">Concierge Security</option>
                    <option value="construction">Construction Site Security</option>
                    <option value="retail">Loss Prevention</option>
                    <option value="cctv">CCTV Monitoring</option>
                    <option value="corporate">Corporate Security</option>
                    <option value="executive">Executive Protection</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all"
                    placeholder="City, State/Province"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-all resize-none"
                    placeholder="Tell us about your security needs..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-sky-500 text-white font-semibold text-lg rounded-lg hover:bg-sky-400 transition-all shadow-lg shadow-sky-500/25"
                >
                  <Send className="w-5 h-5" />
                  Send Request
                </button>

                <p className="text-xs text-slate-600 text-center">
                  We respect your privacy and will never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Contact
